import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="master-ticket-form"
export class MasterTicketFormController extends Controller {
  static targets = [
    "normal", // 通常チケットの表示項目
    "referenceNumber", // 整理番号チケットの表示項目
    "seat", // 座席指定チケットの表示項目
    "noShared", // 在庫共有なしの表示項目
    "shared", // 在庫共有ありの表示項目
    "divided", // 在庫割り当てありの表示項目
    "firstViewDisabled", // 初期表示の非活性項目
    "quantityMax", // 販売予定枚数の表示項目
    "quantity", // 在庫数の表示項目
    "inputQuantityMax", // 販売予定枚数の入力フォーム
    "isDividedArea", // 在庫割り当ての表示項目
    "inputIsDivided", // 在庫割り当てのチェックボックス
  ];
  static values = {
    ticketType: { type: String },
    category: { type: String, default: "no_stock_sharing" },
    firstViewDisabled: { type: Boolean, default: true },
    reserved: { type: Boolean, default: false },
    reservedQuantity: { type: Number, default: 0 },
  };

  connect() {
    this.reView();
  }

  reView() {
    this.#changeTicketTypeView();
    this.#changeCategoryView();
    this.#reflectQuantityMax(this.inputQuantityMaxTarget.value);
    this.#changeIsDividedView();
  }

  // 在庫数計算
  calcQuantity(e) {
    const inputValue = e.currentTarget.value;

    // 数字が入力された場合か未入力の場合に反映する。
    if (/^\d+$/.test(inputValue) || inputValue.length === 0) {
      const quantity = inputValue - this.reservedQuantityValue;

      if (this.hasQuantityTarget) {
        this.quantityTarget.textContent = quantity;
      }

      // 各チケットの販売予定枚数の表示を更新する
      this.#reflectQuantityMax(inputValue);
    }
  }

  // 販売予定枚数の表示を更新する
  #reflectQuantityMax(value) {
    this.quantityMaxTargets.forEach((element) => {
      element.textContent = value;
    });
  }

  setTicketTypeValue(e) {
    // 初期表示項目の非活性の場合は解除する
    if (this.firstViewDisabledValue) {
      this.firstViewDisabledTargets.forEach((element) => {
        if (element.tagName === "BUTTON") {
          element.removeAttribute("aria-disabled", true);
        } else {
          element.removeAttribute("disabled", true);
        }
      });
      this.firstViewDisabledValue = false;
    }

    this.ticketTypeValue = e.currentTarget.value;
    this.#changeTicketTypeView();
    this.#changeIsDividedView();
    // 在庫割り当ての状態で座席チケットの表示に切り替わる場合は、在庫割り当てをOFFにする。
    if (this.ticketTypeValue === "seat" && this.categoryValue === "stock_split") this.inputIsDividedTarget.click();
  }

  // 複数のチケットで在庫を共有する変更処理
  changeIsShared(e) {
    if (e.currentTarget.checked) {
      this.categoryValue = "stock_sharing";
      // チケット名で入力した値をマスターチケット名に反映
      const name = document.getElementById("master_ticket_form_ticket_name").value;
      document.getElementById("master_ticket_form_master_ticket_name").value = name;
      this.#changeIsDividedView();
    } else {
      this.categoryValue = "no_stock_sharing";
    }

    document.getElementById("is_shared_hidden").value = e.currentTarget.checked;
    this.#changeCategoryView();
  }

  // チケットごとに販売予定枚数を割り当てる変更処理
  changeIsDivided(e) {
    const isShared = document.getElementById("master_ticket_form_is_shared");
    if (e.currentTarget.checked) {
      this.categoryValue = "stock_split";
      if (!this.reservedValue) isShared.disabled = true;
    } else {
      this.categoryValue = "stock_sharing";
      if (!this.reservedValue) isShared.disabled = false;
    }

    document.getElementById("is_divided_hidden").value = e.currentTarget.checked;
    this.#changeCategoryView();
  }

  // 在庫割り当ての表示を切り替え
  #changeIsDividedView() {
    // 座席チケットの場合は、在庫割当させないようにする。
    if (this.ticketTypeValue === "seat") {
      this.#hideElements(this.isDividedAreaTargets);
    } else {
      this.#showElements(this.isDividedAreaTargets);
    }
  }

  // チケットの種類変更時の表示切り替え
  #changeTicketTypeView() {
    switch (this.ticketTypeValue) {
      case "normal":
        this.#hideElements(this.referenceNumberTargets);
        this.#hideElements(this.seatTargets);
        this.#showElements(this.normalTargets);
        break;
      case "reference_number":
        this.#hideElements(this.normalTargets);
        this.#hideElements(this.seatTargets);
        this.#showElements(this.referenceNumberTargets);
        break;
      case "seat":
        this.#hideElements(this.normalTargets);
        this.#hideElements(this.referenceNumberTargets);
        this.#showElements(this.seatTargets);
    }
  }

  // 在庫共有変更時の表示切り替え
  #changeCategoryView() {
    switch (this.categoryValue) {
      case "no_stock_sharing":
        this.#hideElements(this.sharedTargets);
        this.#hideElements(this.dividedTargets);
        this.#showElements(this.noSharedTargets);
        break;
      case "stock_sharing":
        this.#hideElements(this.noSharedTargets);
        this.#hideElements(this.dividedTargets);
        this.#showElements(this.sharedTargets);
        break;
      case "stock_split":
        this.#hideElements(this.noSharedTargets);
        this.#hideElements(this.sharedTargets);
        this.#showElements(this.dividedTargets);
    }
  }

  #showElements(elements) {
    elements.forEach((element) => {
      element.style.display = "";
    });
  }

  #hideElements(elements) {
    elements.forEach((element) => {
      element.style.display = "none";
    });
  }
}

// 販売予定枚数の割り当ての入力フォーム
// Connects to data-controller="stock-quantity-form"
export class StockQuantityFormController extends Controller {
  static targets = ["stockQuantityMax", "stockQuantity"];
  static values = { reservedQuantity: { type: Number, default: 0 } };

  calcStockQuantity(e) {
    const inputValue = e.currentTarget.value;

    // 数字が入力された場合か未入力の場合に反映する。
    if (/^\d+$/.test(inputValue) || inputValue.length === 0) {
      const quantity = inputValue - this.reservedQuantityValue;

      if (this.hasStockQuantityMaxTarget) {
        this.stockQuantityMaxTarget.textContent = inputValue;
      }
      if (this.hasStockQuantityTarget) {
        this.stockQuantityTarget.textContent = quantity;
      }
    }
  }
}
